/**
 * CollapsiblePageHeader Component - Row 2 (Page Title Row)
 *
 * Standardized page header for ALL tabs:
 * - Breadcrumb trail with campaign selector (last item)
 * - Icon (lucide-react)
 * - Title (h1 or h2)
 * - Description (collapsible on mobile)
 *
 * Mobile: Collapsed by default, expand button reveals description
 * Desktop: Always expanded
 *
 * Height:
 * - Mobile collapsed: ~100px (doubled from ~50px)
 * - Mobile expanded: ~240px (auto, doubled)
 * - Desktop: ~80-100px (auto)
 *
 * RULES:
 * - NO action buttons allowed in this row
 * - NO filters, search bars, or interactive elements (except expand/collapse)
 * - Description-only content
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, LucideIcon, Info } from 'lucide-react';
import { TabHeaderSelector } from './TabHeaderSelector';
import { ActiveRouteIndicator } from './ActiveRouteIndicator';
import { MobileFixedHeader } from './MobileFixedHeader';
import { Tooltip } from '@shared/components/ui/Tooltip';
import { useIsMobile } from '@shared/hooks';
import type { AppTab } from '../../../types';

export interface CollapsiblePageHeaderProps {
  /**
   * Page icon (Lucide icon component)
   */
  icon: LucideIcon;

  /**
   * Page title
   */
  title: string;

  /**
   * Page description (collapsible on mobile)
   */
  description?: string | React.ReactNode;

  /**
   * Unique key for localStorage (to persist collapse state)
   */
  storageKey?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Optional click handler for the icon (mobile navigation menu)
   * When provided, makes the icon appear clickable on mobile
   */
  onIconClick?: () => void;

  /**
   * Whether to show campaign selector in breadcrumb
   * Default: true (most tabs are campaign-dependent)
   */
  showCampaignSelector?: boolean;

  /**
   * Whether to show route selector in breadcrumb
   * Default: true (available from all tabs)
   */
  showRouteSelector?: boolean;
}

/**
 * Collapsible Page Header - Breadcrumb + Icon + Title + Description
 *
 * @example
 * ```tsx
 * import { Layout } from 'lucide-react';
 *
 * // Basic usage (with campaign selector)
 * <CollapsiblePageHeader
 *   icon={Layout}
 *   title="Organization Management"
 *   description="Browse and manage organizations from Pipedrive"
 *   storageKey="org-mgmt-header"
 * />
 *
 * // With route selector (for route-related tabs)
 * <CollapsiblePageHeader
 *   icon={Map}
 *   title="Route Planning"
 *   description="Calculate and review routes"
 *   showRouteSelector={true}
 * />
 *
 * // Without campaign selector (for dashboard, settings)
 * <CollapsiblePageHeader
 *   icon={Home}
 *   title="Dashboard"
 *   showCampaignSelector={false}
 * />
 * ```
 */
export const CollapsiblePageHeader: React.FC<CollapsiblePageHeaderProps> = ({
  icon: Icon,
  title,
  description,
  storageKey,
  className = '',
  onIconClick,
  showCampaignSelector = true,
  showRouteSelector = true,
}) => {
  const isMobile = useIsMobile();

  // Collapse state - always collapsed on mobile initially
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle icon click - opens mobile sidebar
  const handleIconClick = () => {
    // Dispatch event to open AppShell's mobile sidebar
    window.dispatchEvent(new CustomEvent('open-mobile-sidebar'));
    // Also call the provided onIconClick if any
    onIconClick?.();
  };

  // Load collapse state from localStorage on mount
  useEffect(() => {
    if (storageKey) {
      const stored = localStorage.getItem(`collapsible-header-${storageKey}`);
      if (stored !== null) {
        setIsExpanded(stored === 'true');
      }
    }
  }, [storageKey]);

  // Save collapse state to localStorage
  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (storageKey) {
      localStorage.setItem(`collapsible-header-${storageKey}`, String(newState));
    }
  };

  // On mobile, render the fixed header instead
  if (isMobile) {
    // When collapsed, header is just title row: p-3 (12px) + icon/title row (40px) + p-3 (12px) = ~64px
    // Since all content is now in the expanded section, collapsed height is consistent
    const spacerClass = 'h-16'; // Fixed height for collapsed state (~64px)

    return (
      <>
        {/* Fixed Mobile Header */}
        <MobileFixedHeader
          icon={Icon}
          title={title}
          description={description}
          storageKey={storageKey}
          showCampaignSelector={showCampaignSelector}
          showRouteSelector={showRouteSelector}
        />
        {/* Spacer to push content below fixed header */}
        <div className={spacerClass} />
      </>
    );
  }

  // Desktop version - compact header with tooltip
  return (
    <div
      data-testid="page-header"
      className={`
        bg-gradient-to-r from-dark-surface/50 via-dark-bg/80 to-dark-surface/30 backdrop-blur-sm border-b border-dark-border/25
        p-3 md:py-2 md:px-6
        shrink-0
        ${className}
      `}
    >
      {/* Title Row - always visible */}
      <div className="flex items-center gap-3">
        {/* Icon - Not clickable on desktop */}
        <div
            className="
              flex-shrink-0 w-10 h-10
              rounded-xl bg-gradient-to-br from-brand-500/20 to-brand-700/10
              border border-brand-500/15
              flex items-center justify-center
              relative shadow-lg shadow-brand-900/15
            "
          >
            <Icon className="w-5 h-5 text-brand-400" />
          </div>

        {/* Title with Info Icon Tooltip (description on hover) */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1">
            <h1 className="text-base md:text-lg font-bold tracking-tight text-white whitespace-nowrap truncate">
              {title}
            </h1>

            {/* Info icon with tooltip for description (only for simple string descriptions) */}
            {description && typeof description === 'string' && (
              <Tooltip
                content={description}
                side="right"
                align="center"
              >
                <div className="hidden md:flex items-center justify-center text-dark-muted hover:text-dark-text transition-all duration-150 cursor-help -mt-0.5">
                  <Info className="w-1.5 h-1.5" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
